import { Icon20UsersOutline } from '@vkontakte/icons';
import browser from 'Cloud/browser';
import config from 'Cloud/config';
import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDomainRestrictionsForItems } from 'reactApp/appHelpers/appHelpers';
import { IS_FREE_BIZ_SAAS_USER, IS_PAID_B2B_TARIFF, IS_TABLET_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { seoEnable } from 'reactApp/appHelpers/featuresHelpers/features/seoEnable';
import { openCreateCopyOfNoneditableFileModal } from 'reactApp/components/CreateCopyOfNoneditableFileModal/helper';
import {
    getAccessFileRightsBizOptions,
    getAccessFolderRightsOptionsBiz,
    getBizSaasAccessRightsOptions,
} from 'reactApp/components/SharingNewBiz/SharingNew.constants';
import type { DropdownOptions } from 'reactApp/components/SharingNewBiz/SharingNew.types';
import {
    ACCESS_FOLDER_RIGHTS_NO_DOWNLOAD_OPTIONS,
    ACCESS_FOLDER_RIGHTS_OPTIONS,
    getAccessFileRightsNoDownloadOptions,
    getAccessFileRightsOptions,
    QA_VALUE,
    TOOLTIP_OFFSET,
} from 'reactApp/components/SharingWindow/Sharing.constants';
import { sendGa } from 'reactApp/components/SharingWindow/Sharing.helpers';
import { AccessStatus } from 'reactApp/components/SharingWindow/Sharing.types';
import { SharingDropdown } from 'reactApp/components/SharingWindow/ui/Dropdown/SharingDropdown';
import { getEditorsFor, getIsFileForCoEditing } from 'reactApp/modules/editor/editor.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getFeatureEditingNoneditableFormats } from 'reactApp/modules/features/features.selectors';
import { getItemTypeName } from 'reactApp/modules/file/utils';
import type { XrayItemType } from 'reactApp/modules/home/home.types';
import { weblinkHandleAccessRightChangeSaga } from 'reactApp/modules/modifying/modifying.actions';
import type { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import type { DownloadLimit, PublicFile } from 'reactApp/modules/public/public.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { isEditorStorage } from 'reactApp/modules/storage/storage.helpers';
import { getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { AccessRights } from 'reactApp/types/Tree';
import { useDisableSeoTooltip } from 'reactApp/ui/DisableSeoTooltip/hooks/useDisableSeoTooltip';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import { SharingTooltipPromo } from 'reactApp/ui/SharingTooltips/SharingTooltipPromo';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './SharingOptions.css';

interface Props {
    item: PublishItem;
    onUnpublish?: () => void;
    isReadOnly: boolean;
    isReadOnlyProperty: boolean;
    disableDownload?: boolean;
    redesign?: boolean;
    showSharingTooltipPromo?: boolean;
    downloadLimits?: DownloadLimit;
    isBizUser?: boolean;
    isDownloadable?: boolean;
    xrayItemType: XrayItemType;
}

const IS_FREE_B2B_BIZ_EDITORS_USER = config.get('FREE_B2B_BIZ_EDITORS_USER');

const getAccessRights = (
    isReadOnly: boolean,
    isFolder: boolean,
    isPublicReadWrite: boolean,
    isDownloadable: boolean,
    isFreeBizUser: boolean,
    isCommentable: boolean,
    isB2BUser: boolean
): AccessStatus => {
    if (isCommentable) {
        return AccessStatus.Comment;
    }

    if (isFreeBizUser && isPublicReadWrite && !isFolder) {
        return AccessStatus.ReadOnly;
    }

    if (isDownloadable) {
        return AccessStatus.Download;
    }

    if (isPublicReadWrite && !isReadOnly) {
        return isFolder ? AccessStatus.Upload : AccessStatus.Edit;
    }

    /* B2B-5926: в соответствии с бизнес-логикой и текстом опции "Смотреть и скачивать"
    заменяем у b2c дефолтную опцию с AccessStatus.ReadOnly на AccessStatus.Download.
    Миграция реализует замену в БД, но UI должен также подстраховаться.*/

    return isB2BUser ? AccessStatus.ReadOnly : AccessStatus.Download;
};

// eslint-disable-next-line max-lines-per-function
const SharingOptionAccessRights = memo(
    // eslint-disable-next-line max-lines-per-function, complexity
    ({
        item,
        onUnpublish,
        isReadOnlyProperty,
        isReadOnly,
        disableDownload = false,
        redesign,
        showSharingTooltipPromo,
        downloadLimits,
        isBizUser,
        isDownloadable,
        xrayItemType,
    }: Props) => {
        const ref = useRef<HTMLDivElement>(null);
        const dispatch = useDispatch();
        const isPhone = useSelector(EnvironmentSelectors.isPhone);
        const currentStorage = useSelector(getCurrentStorage);
        const storage = isEditorStorage(currentStorage) ? EStorageType.home : currentStorage;
        const isFolder = item?.isFolder;
        const editingNoneEditableFormatsFeature = useSelector((state: RootState) =>
            getFeatureEditingNoneditableFormats(state, { file: item as unknown as PublicFile, storage })
        );
        const convertExt = editingNoneEditableFormatsFeature?.ext || '';
        const convertedStorage = editingNoneEditableFormatsFeature?.storage;
        const isConvertFileForCoEditing = useSelector((state: RootState) => getIsFileForCoEditing(state, { ...item, ext: convertExt }));
        const isFileForCoEditing = useSelector((state: RootState) => getIsFileForCoEditing(state, item));
        const isEditable = !!useSelector((state: RootState) => getEditorsFor(state, convertExt ? { ...item, ext: convertExt } : item))
            ?.length;
        const isDocumentForEdit =
            (item?.kind === 'document' && IS_FREE_B2B_BIZ_EDITORS_USER) ||
            isFileForCoEditing ||
            (isConvertFileForCoEditing && !(IS_TABLET_BROWSER || browser.isIpadOs()) && isEditable);
        const accessEditRight = isFolder ? AccessStatus.Upload : AccessStatus.Edit;
        const isB2BUser = useSelector(UserSelectors.isB2BUser);

        const isCommentable = item?.weblinkAccessRights === AccessRights.comment;

        const isPublic = Boolean(item?.weblink);
        const parent = useSelector((state: RootState) => getStorageItemById(state, storage, item?.parent));

        const isReadOnlyParent = parent && 'isReadOnly' in parent && parent?.isReadOnly;

        let options: DropdownOptions;

        if (isFolder && isBizUser) {
            options = getAccessFolderRightsOptionsBiz();
        } else if (isFolder && !isBizUser) {
            options = disableDownload ? ACCESS_FOLDER_RIGHTS_NO_DOWNLOAD_OPTIONS : ACCESS_FOLDER_RIGHTS_OPTIONS;
        } else if (!isFolder && isBizUser) {
            options = !IS_PAID_B2B_TARIFF ? getBizSaasAccessRightsOptions() : getAccessFileRightsBizOptions();
        } else {
            options = disableDownload ? getAccessFileRightsNoDownloadOptions() : getAccessFileRightsOptions();
        }

        const isPublicReadWrite = item?.weblinkAccessRights === AccessRights.write;
        const { isDomainAndEditRestricted, isDomainAndDownloadRestricted } = getDomainRestrictionsForItems(item);

        const accessRights = getAccessRights(
            isReadOnlyProperty,
            isFolder,
            isPublicReadWrite,
            Boolean(isDownloadable),
            IS_FREE_BIZ_SAAS_USER,
            isCommentable,
            isB2BUser
        );

        const isHidden = (isFolder && isReadOnly) || (!isFolder && (!isEditable || isReadOnlyParent || !isDocumentForEdit) && !isBizUser);

        const weblinkSeoEnabled = item?.weblinkSeoEnabled && seoEnable;

        const itemName = getItemTypeName(item);

        const { tooltip, handleShowTip } = useDisableSeoTooltip(ref, itemName, weblinkSeoEnabled);

        const renderAccessRightsDropdown = useCallback(
            (onClick): ReactElement => {
                let list = Object.values(options).map(({ id, dropdownText, text }) => ({
                    id,
                    text: dropdownText ?? text,
                    check: id === accessRights,
                    qaValue: id,
                }));

                const needOmitEdit = (!isDocumentForEdit && !isFolder) || isReadOnlyParent || isDomainAndEditRestricted;
                if (needOmitEdit) {
                    list = list.filter((item) => item.id !== accessEditRight);
                }

                const needOmitDownload = isDomainAndDownloadRestricted;
                if (needOmitDownload) {
                    list = list.filter((item) => item.id !== AccessStatus.Download);
                }

                return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={styles.dropdownMenu} />;
            },
            [
                accessRights,
                isDocumentForEdit,
                accessEditRight,
                isFolder,
                isReadOnlyParent,
                options,
                isDomainAndEditRestricted,
                isDomainAndDownloadRestricted,
            ]
        );

        const handleAccessRightsChange = useCallback(
            (newAccessRight): void => {
                if (!isPublic || accessRights === newAccessRight) {
                    return;
                }

                sendGa(accessRights === AccessStatus.ReadOnly ? 'access-rights' : 'ta-access-rights');
                sendPaymentGa({
                    eventCategory: ECategoryGa.public,
                    action: 'edit-type-access',
                    type_access: newAccessRight,
                    public_id: item?.weblink,
                });

                if (isConvertFileForCoEditing && convertedStorage) {
                    onUnpublish?.();
                    openCreateCopyOfNoneditableFileModal({
                        file: item as unknown as PublicFile,
                        editingNoneEditableFormats: { storage: convertedStorage, ext: convertExt },
                        fromSharePopup: true,
                    });
                    return;
                }

                dispatch(
                    weblinkHandleAccessRightChangeSaga({
                        item,
                        isEditable,
                        isReadOnlyProperty,
                        newAccessRight,
                        storage,
                        xrayItemType,
                    })
                );
            },
            [
                accessRights,
                convertExt,
                convertedStorage,
                dispatch,
                isConvertFileForCoEditing,
                isEditable,
                isPublic,
                isReadOnlyProperty,
                item,
                onUnpublish,
                storage,
                xrayItemType,
            ]
        );

        if (isHidden) {
            return null;
        }

        const accessItem = options[accessRights];

        return (
            <div
                className={classNames(styles.rightItem, { [styles.rightItem_disabled]: weblinkSeoEnabled })}
                data-qa-item={QA_VALUE.accessItem}
                onClick={handleShowTip}
            >
                <span className={classNames(styles.rightTitle, { [styles.tabs]: redesign })}>
                    {redesign && <Icon20UsersOutline />}
                    Пользователи могут
                </span>
                <div className={styles.dropdown} ref={ref}>
                    <Dropdown
                        withinDialog
                        theme="octavius"
                        qaValue={accessItem?.id}
                        as={SharingDropdown}
                        value={isPhone ? accessItem?.text : accessItem?.text.toLowerCase()}
                        content={renderAccessRightsDropdown}
                        onClickItem={handleAccessRightsChange}
                        placement={tooltipPlacements.BOTTOM_LEFT}
                        qaValueContent={QA_VALUE.accessDropdownContent}
                        tooltipOffset={TOOLTIP_OFFSET}
                        animatedTooltip
                        description="Пользователи могут"
                        tooltipClassName={isPhone ? styles.tooltip : undefined}
                        isPhone={isPhone}
                        noPageOffset
                        redesign={redesign}
                        disabled={weblinkSeoEnabled}
                    />
                </div>
                {isFolder && showSharingTooltipPromo && (
                    <SharingTooltipPromo isMobile={isPhone} target={ref} downloadLimits={downloadLimits} />
                )}
                {tooltip}
            </div>
        );
    }
);

SharingOptionAccessRights.displayName = 'SharingOptionAccessRights';

export default SharingOptionAccessRights;
